import { useMemo, useState } from 'react'
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, X,
} from 'lucide-react'
import { useCancelFollowUp, useGHLContactNames } from '@/hooks/useGHLDashboard'
import {
  type GHLFollowUpGroupedRow,
  type GHLFollowUpStats,
  formatFireAt,
  formatRelativeTime,
  formatCost,
  calculateAnalysisCost,
  formatTokenCount,
  getFollowUpStatusColor,
  formatStepBadge,
} from '@/lib/ghl-dashboard'
import { FollowUpStatsCards } from './FollowUpStatsCards'
import { FollowUpTimeline } from './FollowUpTimeline'
import { ContactAvatar } from './ContactAvatar'

interface Props {
  readonly data: GHLFollowUpGroupedRow[]
  readonly count: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly onPageChange: (page: number) => void
  readonly stats: GHLFollowUpStats | null | undefined
  readonly statsLoading: boolean
  readonly statusFilter: string
  readonly onStatusFilterChange: (status: string) => void
  readonly locationNames?: Map<string, string>
}

function LoadingSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: 9 }).map((_, j) => (
            <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

/**
 * Inline cell renderer for the "Gate" column.
 *
 * Three render states:
 *   - decision === 'send'  → green secondary badge "✓ send" + reason below
 *   - decision === 'skip'  → muted outline badge "⊘ skipped" + reason below
 *   - decision === null    → muted em-dash (gate did not run)
 *
 * Mobile (<640px): label collapses; icon + aria-label carry the full text.
 * Reasons exceeding column width truncate with `...` and are shown in full
 * via the native `title` attribute (matches existing pattern at line 189/191).
 */
function GateCell({ decision, reason }: { decision: 'send' | 'skip' | null; reason: string | null }) {
  if (decision === null) {
    return (
      <span className="text-muted-foreground/40" aria-label="Gate did not run">
        —
      </span>
    )
  }
  if (decision === 'send') {
    return (
      <div className="text-xs">
        <Badge variant="secondary" className="text-green-700 bg-green-50">
          <span aria-label={`Gate: send — ${reason ?? ''}`}>
            ✓ <span className="hidden sm:inline">send</span>
          </span>
        </Badge>
        {reason && (
          <p
            className="text-muted-foreground truncate max-w-[140px] hidden sm:block"
            title={reason}
          >
            {reason}
          </p>
        )}
      </div>
    )
  }
  // decision === 'skip'
  return (
    <div className="text-xs">
      <Badge variant="outline" className="text-muted-foreground">
        <span aria-label={`Gate: skipped — ${reason ?? ''}`}>
          ⊘ <span className="hidden sm:inline">skipped</span>
        </span>
      </Badge>
      {reason && (
        <p
          className="text-muted-foreground truncate max-w-[140px] hidden sm:block"
          title={reason}
        >
          {reason}
        </p>
      )}
    </div>
  )
}

export function FollowUpTab({
  data, count, page, pageSize, isLoading, onPageChange,
  stats, statsLoading, statusFilter, onStatusFilterChange,
}: Props) {
  const [selectedRow, setSelectedRow] = useState<GHLFollowUpGroupedRow | null>(null)
  const [cancelTarget, setCancelTarget] = useState<GHLFollowUpGroupedRow | null>(null)
  const cancelMutation = useCancelFollowUp()

  const totalPages = Math.ceil(count / pageSize)
  const from = page * pageSize + 1
  const to = Math.min((page + 1) * pageSize, count)

  // Resolve contact names for the visible page
  const contactIds = useMemo(
    () => [...new Set(data.map((row) => row.contact_id).filter(Boolean))],
    [data]
  )
  const effectiveLocationId = data[0]?.location_id
  const { data: contactNames } = useGHLContactNames(contactIds, effectiveLocationId)

  function handleCancelConfirm() {
    if (!cancelTarget) return
    cancelMutation.mutate({ id: cancelTarget.id }, {
      onSettled: () => setCancelTarget(null),
    })
  }

  return (
    <div className="space-y-6">
      <FollowUpStatsCards stats={stats} isLoading={statsLoading} />

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={statusFilter} onValueChange={onStatusFilterChange}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="skipped">Skipped</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">Auto-refreshing every 15 seconds</p>
      </div>

      {/* Table — one row per opportunity (server-side grouped) */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Contact</TableHead>
              <TableHead>Scheduled For</TableHead>
              <TableHead>Quoted</TableHead>
              <TableHead>Pickup</TableHead>
              <TableHead>Steps</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Gate</TableHead>
              <TableHead>Message</TableHead>
              <TableHead className="text-right">AI Cost</TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <LoadingSkeleton />}
            {!isLoading && data.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                  No follow-up messages found
                </TableCell>
              </TableRow>
            )}
            {!isLoading && data.map((row) => {
              const statusColor = getFollowUpStatusColor(row.status)
              const cost = calculateAnalysisCost(row.input_tokens, row.output_tokens, row.model_used)
              const remainingSteps = row.total_steps - 1
              const name = contactNames?.get(row.contact_id)

              return (
                <TableRow
                  key={row.opportunity_id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setSelectedRow(row)}
                >
                  <TableCell>
                    <ContactAvatar
                      size="sm"
                      showName
                      firstName={name?.firstName}
                      lastName={name?.lastName}
                      fullName={name?.fullName}
                      contactId={row.contact_id}
                    />
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    <span className={row.status === 'pending' ? 'text-foreground' : 'text-muted-foreground'}>
                      {formatFireAt(row.fire_at)}
                    </span>
                    {remainingSteps > 0 && (
                      <Badge variant="secondary" className="ml-2 text-[10px]">
                        +{remainingSteps} more
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {row.quote_date ? formatRelativeTime(row.quote_date) : '--'}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {row.pickup_date ?? '--'}
                  </TableCell>
                  <TableCell className="text-xs">
                    <span className="text-green-600">{row.sent_steps} sent</span>
                    {row.pending_steps > 0 && <span className="text-muted-foreground"> / {row.pending_steps} pending</span>}
                    {row.cancelled_steps > 0 && <span className="text-muted-foreground/60"> / {row.cancelled_steps} cancelled</span>}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${statusColor.bg} ${statusColor.text} text-xs`}>
                      {statusColor.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <GateCell decision={row.gate_decision} reason={row.gate_reason} />
                  </TableCell>
                  <TableCell className="text-sm max-w-[300px]">
                    {row.sms_message ? (
                      <p className="truncate text-muted-foreground" title={row.sms_message}>{row.sms_message}</p>
                    ) : row.skip_reason ? (
                      <p className="truncate text-xs text-muted-foreground/60 italic" title={row.skip_reason}>{row.skip_reason}</p>
                    ) : (
                      <span className="text-muted-foreground/40">--</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">
                    {row.input_tokens ? (
                      <div>
                        <div>{formatCost(cost)}</div>
                        <div className="text-[10px] text-muted-foreground/60">
                          {formatTokenCount(row.input_tokens)}/{formatTokenCount(row.output_tokens)}
                        </div>
                      </div>
                    ) : <span>--</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    {row.status === 'pending' && (
                      <Button
                        variant="ghost" size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600"
                        title="Cancel this follow-up"
                        disabled={cancelMutation.isPending}
                        onClick={(e) => { e.stopPropagation(); setCancelTarget(row) }}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination — counts are now opportunities, not individual steps */}
      {count > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Showing {from}-{to} of {count.toLocaleString()} opportunities</p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => onPageChange(0)}>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => onPageChange(page - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm px-2">Page {page + 1} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => onPageChange(page + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => onPageChange(totalPages - 1)}>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Slide-in detail panel */}
      <Sheet open={!!selectedRow} onOpenChange={(open) => { if (!open) setSelectedRow(null) }}>
        <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Follow-Up Timeline</SheetTitle>
          </SheetHeader>

          {selectedRow && (
            <>
              {/* Context header */}
              <div className="mt-4 flex gap-6 text-sm border-b pb-4">
                <div>
                  <p className="text-xs text-muted-foreground">Quoted</p>
                  <p className="font-medium">
                    {selectedRow.quote_date ? new Date(selectedRow.quote_date).toLocaleDateString() : '--'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pickup Date</p>
                  <p className="font-medium">{selectedRow.pickup_date ?? 'Not set'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Steps</p>
                  <p className="font-medium">
                    {selectedRow.sent_steps}/{selectedRow.total_steps} sent
                  </p>
                </div>
              </div>

              {/* Timeline (fetches all steps for this opportunity) */}
              <div className="mt-4">
                <FollowUpTimeline opportunityId={selectedRow.opportunity_id} />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(open) => { if (!open) setCancelTarget(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel scheduled SMS?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the follow-up SMS for step{' '}
              <span className="font-medium text-foreground">
                {cancelTarget ? formatStepBadge(cancelTarget.step_number, cancelTarget.step_label) : ''}
              </span>{' '}
              scheduled for{' '}
              <span className="font-medium text-foreground">
                {cancelTarget ? formatFireAt(cancelTarget.fire_at) : ''}
              </span>.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={cancelMutation.isPending}
              onClick={handleCancelConfirm}
            >
              {cancelMutation.isPending ? 'Cancelling...' : 'Yes, cancel it'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
